// src/pages/lead/LeadTeam.jsx
// Team Lead can VIEW their team and REQUEST removal of a member.
// Actual removal requires Admin approval (creates teamRemovalRequests).
// This prevents unilateral team modifications and ensures audit trail.

import React, { useState } from "react";
import { useLeadData } from "../../context/LeadContext";
import { LEAD_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";
import { createRemovalRequest } from "../../hooks/useFirestoreExtensions";
import { useAuth } from "../../context/AuthContext";

export default function LeadTeam() {
  const { userProfile } = useAuth();
  const { members, membersLoading: loading } = useLeadData();
  const [removing, setRemoving] = useState({});
  const [msg, setMsg] = useState("");

  async function handleRemove(member) {
    if (
      !window.confirm(
        `Request removal of ${member.displayName} from your team?\n\nThis will send a removal request to Admin for approval.`,
      )
    )
      return;

    setRemoving((p) => ({ ...p, [member.uid]: true }));
    setMsg("");

    try {
      // Create a removal request instead of direct deletion
      await createRemovalRequest({
        employeeId: member.employeeId,
        employeeUid: member.uid,
        employeeName: member.displayName,
        teamLeadId: userProfile.uid,
        teamLeadName: userProfile.displayName,
        reason: "",
      });

      setMsg(
        `✅ Removal request for ${member.displayName} sent to Admin. Awaiting approval.`,
      );
      // Auto-clear message after 4 seconds
      setTimeout(() => setMsg(""), 4000);
    } catch (err) {
      console.error("[LeadTeam] removal request error:", err);
      setMsg(`❌ Failed to send removal request: ${err.message}`);
    }

    setRemoving((p) => ({ ...p, [member.uid]: false }));
  }

  return (
    <Layout navItems={LEAD_NAV} panelLabel="Team Lead Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">My Team</h1>
          <p className="page-subtitle">
            {userProfile?.department} · {members.length} member
            {members.length !== 1 ? "s" : ""}
          </p>
        </div>
        {/* NOTE: Adding members is an Admin-only action.
            Ask your Admin to assign employees to your team. */}
      </div>

      {msg && (
        <div
          className={msg.startsWith("✅") ? "form-success" : "form-error"}
          style={{ marginBottom: "1rem" }}
        >
          {msg}
        </div>
      )}

      {loading ? (
        <div className="card-loading">Loading team…</div>
      ) : members.length === 0 ? (
        <div className="card-empty" style={{ marginTop: "2rem" }}>
          No team members yet. Contact your Admin to assign employees to your
          team.
        </div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>ID</th>
                <th>Email</th>
                <th>Designation</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.uid}>
                  <td>
                    <div className="table-user-cell">
                      <div className="table-avatar">
                        {m.displayName?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div>{m.displayName}</div>
                        <div
                          style={{
                            fontSize: "0.72rem",
                            color: "var(--text-muted)",
                          }}
                        >
                          {m.department}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.8rem",
                    }}
                  >
                    {m.employeeId || "—"}
                  </td>
                  <td style={{ fontSize: "0.8rem" }}>{m.email}</td>
                  <td>{m.designation || "—"}</td>
                  <td>
                    <span
                      className={`status-pill status-pill--${m.isActive ? "green" : "red"}`}
                    >
                      {m.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-ghost-sm"
                      onClick={() => handleRemove(m)}
                      disabled={!!removing[m.uid]}
                      style={{
                        cursor: removing[m.uid] ? "not-allowed" : "pointer",
                      }}
                    >
                      {removing[m.uid] ? "Removing…" : "Remove"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
